import { Optional } from '@vimeo/core/types/generic';

/**
 * npm/detect-browser modified fork.
 */

export enum ESupportedBrowser {
  EDGE_CHROMIUM = 'edge-chromium',
  FIREFOX = 'firefox',
  CHROME = 'chrome',
  OPERA = 'opera',
  SAFARI = 'safari',
  IOS = 'ios',
}

export const NormalizedSupportedBrowser = {
  [ESupportedBrowser.EDGE_CHROMIUM]: 'Edge',
  [ESupportedBrowser.FIREFOX]: 'Firefox',
  [ESupportedBrowser.CHROME]: 'Chrome',
  [ESupportedBrowser.OPERA]: 'Opera',
  [ESupportedBrowser.SAFARI]: 'Safari',
};

export type TBrowser =
  | 'aol'
  | 'edge'
  | 'yandexbrowser'
  | 'vivaldi'
  | 'kakaotalk'
  | 'samsung'
  | 'silk'
  | 'miui'
  | 'beaker'
  | 'edge-chromium'
  | 'chrome'
  | 'chromium-webview'
  | 'phantomjs'
  | 'crios'
  | 'firefox'
  | 'fxios'
  | 'opera-mini'
  | 'opera'
  | 'ie'
  | 'bb10'
  | 'android'
  | 'ios'
  | 'safari'
  | 'facebook'
  | 'instagram'
  | 'ios-webview'
  | 'searchbot';

export type TOperatingSystem =
  | 'iOS'
  | 'Android OS'
  | 'BlackBerry OS'
  | 'Windows Mobile'
  | 'Amazon OS'
  | 'Windows 3.11'
  | 'Windows 95'
  | 'Windows 98'
  | 'Windows 2000'
  | 'Windows XP'
  | 'Windows Server 2003'
  | 'Windows Vista'
  | 'Windows 7'
  | 'Windows 8'
  | 'Windows 8.1'
  | 'Windows 10'
  | 'Windows ME'
  | 'Open BSD'
  | 'Sun OS'
  | 'Linux'
  | 'Mac OS'
  | 'QNX'
  | 'BeOS'
  | 'OS/2'
  | 'Chrome OS'
  | 'Search Bot';

export interface IDeviceInfo {
  readonly name: TBrowser | 'node';
  readonly version: string;
  readonly os: TOperatingSystem;
  readonly isMobile: boolean;
}

export interface IBotInfo {
  readonly name: 'bot';
  readonly version: null;
  readonly os: null;
}

const SEARCHBOX_UA_REGEX: RegExp =
  /alexa|bot|crawl(er|ing)|facebookexternalhit|feedburner|google web preview|nagios|postrank|pingdom|slurp|spider|yahoo!|yandex/;
const SEARCHBOT_OS_REGEX: RegExp =
  /(nuhk)|(Googlebot)|(Yammybot)|(Openbot)|(Slurp)|(MSNBot)|(Ask Jeeves\/Teoma)|(ia_archiver)/;
const REQUIRED_VERSION_PARTS: number = 3;

const USER_AGENT_RULES: Array<[string, RegExp]> = [
  ['aol', /AOLShield\/([0-9\._]+)/],
  ['edge', /Edge\/([0-9\._]+)/],
  ['yandexbrowser', /YaBrowser\/([0-9\._]+)/],
  ['vivaldi', /Vivaldi\/([0-9\.]+)/],
  ['kakaotalk', /KAKAOTALK\s([0-9\.]+)/],
  ['samsung', /SamsungBrowser\/([0-9\.]+)/],
  ['silk', /\bSilk\/([0-9._-]+)\b/],
  ['miui', /MiuiBrowser\/([0-9\.]+)$/],
  ['beaker', /BeakerBrowser\/([0-9\.]+)/],
  ['edge-chromium', /Edg\/([0-9\.]+)/],
  ['chromium-webview', /(?!Chrom.*OPR)wv\).*Chrom(?:e|ium)\/([0-9\.]+)(:?\s|$)/],
  ['chrome', /(?!Chrom.*OPR)Chrom(?:e|ium)\/([0-9\.]+)(:?\s|$)/],
  ['phantomjs', /PhantomJS\/([0-9\.]+)(:?\s|$)/],
  ['crios', /CriOS\/([0-9\.]+)(:?\s|$)/],
  ['firefox', /Firefox\/([0-9\.]+)(?:\s|$)/],
  ['fxios', /FxiOS\/([0-9\.]+)/],
  ['opera-mini', /Opera Mini.*Version\/([0-9\.]+)/],
  ['opera', /Opera\/([0-9\.]+)(?:\s|$)/],
  ['opera', /OPR\/([0-9\.]+)(:?\s|$)/],
  ['ie', /Trident\/7\.0.*rv\:([0-9\.]+).*\).*Gecko$/],
  ['ie', /MSIE\s([0-9\.]+);.*Trident\/[4-7].0/],
  ['ie', /MSIE\s(7\.0)/],
  ['bb10', /BB10;\sTouch.*Version\/([0-9\.]+)/],
  ['android', /Android\s([0-9\.]+)/],
  ['ios', /Version\/([0-9\._]+).*Mobile.*Safari.*/],
  ['safari', /Version\/([0-9\._]+).*Safari/],
  ['facebook', /FBAV\/([0-9\.]+)/],
  ['instagram', /Instagram\s([0-9\.]+)/],
  ['ios-webview', /AppleWebKit\/([0-9\.]+).*Mobile/],
  ['ios-webview', /AppleWebKit\/([0-9\.]+).*Gecko\)$/],
  ['searchbot', SEARCHBOX_UA_REGEX],
];

const OS_RULES: Array<[string, RegExp]> = [
  ['iOS', /iP(hone|od|ad)/],
  ['Android OS', /Android/],
  ['BlackBerry OS', /BlackBerry|BB10/],
  ['Windows Mobile', /IEMobile/],
  ['Amazon OS', /Kindle/],
  ['Windows 3.11', /Win16/],
  ['Windows 95', /(Windows 95)|(Win95)|(Windows_95)/],
  ['Windows 98', /(Windows 98)|(Win98)/],
  ['Windows 2000', /(Windows NT 5.0)|(Windows 2000)/],
  ['Windows XP', /(Windows NT 5.1)|(Windows XP)/],
  ['Windows Server 2003', /(Windows NT 5.2)/],
  ['Windows Vista', /(Windows NT 6.0)/],
  ['Windows 7', /(Windows NT 6.1)/],
  ['Windows 8', /(Windows NT 6.2)/],
  ['Windows 8.1', /(Windows NT 6.3)/],
  ['Windows 10', /(Windows NT 10.0)/],
  ['Windows ME', /Windows ME/],
  ['Open BSD', /OpenBSD/],
  ['Sun OS', /SunOS/],
  ['Chrome OS', /CrOS/],
  ['Linux', /(Linux)|(X11)/],
  ['Mac OS', /(Mac_PowerPC)|(Macintosh)/],
  ['QNX', /QNX/],
  ['BeOS', /BeOS/],
  ['OS/2', /OS\/2/],
  ['Search Bot', SEARCHBOT_OS_REGEX],
];

/**
 * Get node version.
 */
function getNodeVersion(): Optional<IDeviceInfo> {
  return typeof process !== 'undefined' && process.version
    ? {
        name: 'node',
        os: process.platform as TOperatingSystem,
        version: process.version.slice(1),
        isMobile: false,
      }
    : null;
}

/**
 * Try to detect browser information based on user agent or environment.
 */
export function detectBrowser(userAgent?: Optional<string>): Optional<IDeviceInfo | IBotInfo> {
  if (userAgent) {
    return parseUserAgent(userAgent);
  }

  if (typeof navigator !== 'undefined') {
    return parseUserAgent(navigator.userAgent);
  }

  return getNodeVersion();
}

/**
 * Detect browser information from user agent.
 */
function parseUserAgent(ua: string): Optional<IDeviceInfo | IBotInfo> {
  // Original comment:
  // opted for using reduce here rather than Array#first with a regex.test call
  // this is primarily because using the reduce we only perform the regex
  // execution once rather than once for the test and for the exec again below
  // probably something that needs to be benchmarked though

  const matchedRule: [string, RegExpMatchArray] =
    ua !== '' &&
    USER_AGENT_RULES.reduce((matched: any, current: [string, RegExp]) => {
      const browser = current[0];
      const regex = current[1];

      if (matched) {
        return matched;
      }

      const uaMatch: Optional<RegExpExecArray> = regex.exec(ua);

      return !!uaMatch && [browser, uaMatch];
    }, false);

  if (!matchedRule) {
    return null;
  }

  const name: string = matchedRule[0];
  const match: RegExpMatchArray = matchedRule[1];

  if (name === 'searchbot') {
    return { name: 'bot', version: null, os: null };
  }

  let versionParts: Optional<Array<string>> = match[1] ? match[1].split(/[._]/).slice(0, 3) : null;

  // Handle short versions cases (0.1, 1 etc).
  if (versionParts) {
    if (versionParts.length < REQUIRED_VERSION_PARTS) {
      versionParts = versionParts.concat(
        createVersionParts(REQUIRED_VERSION_PARTS - versionParts.length),
      );
    }
  } else {
    versionParts = [];
  }

  return {
    name,
    version: versionParts.join('.'),
    os: detectOS(ua),
    isMobile: detectIfMobile(ua),
  } as IDeviceInfo;
}

/**
 * Detect operating system from provided user agent.
 */
function detectOS(ua: string): Optional<string> {
  for (let it = 0, count = OS_RULES.length; it < count; it += 1) {
    const [os, regex] = OS_RULES[it];

    if (regex.test(ua)) {
      return os;
    }
  }

  return null;
}

/**
 * Detect if on mobile from provided user agent.
 * Checks for the presence of Mobi in user agent, as recommended by MDN docs
 * https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent
 */
function detectIfMobile(ua: string): boolean {
  return /Mobi/i.test(ua);
}

/**
 * Internal utility for detected format matching config.
 */
function createVersionParts(count: number): Array<string> {
  const output: Array<string> = [];

  for (let it = 0; it < count; it += 1) {
    output.push('0');
  }

  return output;
}
